<template>
    <div class="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
        <div class="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap px-4 py-5 sm:p-6">
            <div class="ml-4 mt-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Vehicle</h3>
            </div>
        </div>

        <div class="px-4 py-5 sm:p-6">
            <div v-show="!state.vehicle_form.show_fields" class="mb-4">
                <Message :active="state.find_vehicle_error" :heading="state.find_vehicle_error" type="error" class="mb-8" />
                <Message :active="state.search_message" :heading="state.search_message" type="info" class="mb-8" />

                <div class="w-full flex items-center flex-wrap sm:flex-nowrap">
                    <div class="w-full">
                        <label for="search_vrm" class="block text-sm font-medium text-gray-700">Vehicle number plate & mileage</label>
                        <div class="mt-1 flex rounded-md shadow-sm">
                            <div class="relative flex items-stretch flex-grow focus-within:z-10 mr-2">
                                <div class="min-w-0 flex-1 px-0 grid grid-cols-2 gap-2">
                                    <input v-model="state.search_vrm" type="text" name="search_vrm" id="search_vrm" class="border border-gray-300 focus:ring-blue-500 focus:border-blue-500 block w-full rounded-md pl-3 sm:text-sm border-gray-300" :class="{'border-red-700 bg-red-50 text-red-900 placeholder-red-600': state.search_vrm_error}" placeholder="Number plate" />
                                    <input v-model="state.search_mileage" type="number" name="search_mileage" id="search_mileage" class="border border-gray-300 focus:ring-blue-500 focus:border-blue-500 block w-full rounded-md pl-3 sm:text-sm border-gray-300" :class="{'border-red-700 bg-red-50 text-red-900 placeholder-red-600': state.search_mileage_error}" placeholder="Mileage" />
                                </div>
                            </div>
                            <button v-on:click="checkExistingVehicles('search')" type="button" class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm text-center font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                                <span v-if="!state.search_loading">Search</span>
                                <LoadingIcon v-if="state.search_loading" class="h-5 w-5 animate-reverse-spin" />
                            </button>
                        </div>
                    </div>
                    <button v-on:click="state.vehicle_form.show_fields = !state.vehicle_form.show_fields" class="font-medium text-sm text-blue-600 whitespace-nowrap px-4 py-2 pr-0 mt-6">Enter manually</button>
                </div>
            </div>
            <FormField v-show="state.vehicle_form.show_fields" v-for="(field, key) in state.vehicle_form.fields" :key="key" :field="field" namespace="vehicle" :name="key" class="mb-4" />
        </div>
    
        <div class="px-4 py-4 sm:px-6 text-right">
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => checkExistingVehicles('submit')" :disabled="!next_button_active">Next</Button>
        </div>

        <!-- EXISTING FINANCE MODAL-->
        <Modal v-on:close="state.existing_finance_error_show = false" :active="state.existing_finance_error_show" size="xl">
            <div>
                <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">{{ state.existing_finance_error.heading }}</DialogTitle>
                    <div class="my-2">
                        <p class="text-sm text-gray-500">{{ state.existing_finance_error.body }}</p>
                        <p class="text-sm text-gray-500">{{ `` }}</p>
                    </div>
                </div>
            </div>
            <div class="mt-5 sm:mt-6">
                <Button v-if="!state.application_pending" type="button" color="red" :onClick="clearVehicleDataError">Dismiss</Button>
            </div>
        </Modal>

        <!-- EXISTING VEHICLE MODAL-->
        <Modal v-on:close="state.existing_vehicle_modal_active = false" :active="state.existing_vehicle_modal_active" size="md">
            <div>
                <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">{{ state.existing_vehicle_modal_heading }}</DialogTitle>
                    <div class="my-2">
                        <p class="text-sm text-gray-500">{{ state.existing_vehicle_modal_body }}</p>
                        <p class="text-sm text-gray-500">{{ `` }}</p>
                    </div>
                </div>
            </div>
            <div class="mt-5 sm:mt-6">
                <Button v-if="!state.application_pending" type="button" color="blue" class="mb-2" :onClick="existingVehicleAction">Add vehicle again</Button>
                <Button v-if="!state.application_pending" type="button" color="text-blue" :onClick="cancelSearch">Cancel</Button>
            </div>
        </Modal>
    </div>
</template>

<script>
import { useVehicleStore } from '@sto/vehicle.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'
import { useUserStore } from '@sto/user.store'
import * as VehicleService from '@ser/vehicle'
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import LoadingIcon from '@ico/LoadingIcon.vue'
import { isValid, getPayload, getUserOrganisationType } from '@lib/helpers'
import { useRoute } from 'vue-router'
import config from '@/config'
import Message from "@glo/Message.vue";
export default {
    components: {
        Message,
        LoadingIcon
    },
    setup() {
        const VEHICLE = useVehicleStore()
        const ROUTE = useRoute()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()
        const USER_STORE = useUserStore()

        const vehicle_id = computed(() => {
            return VEHICLE?.vehicle?.id || null
        })

        const vehicle_data = computed(() => {
            return VEHICLE?.vehicle || null
        })

        const vehicle_asset_id = computed(() => {
            return VEHICLE?.vehicle?.asset?.id || null
        })

        const show_tax_applies_toggle = computed(() => {
            return state?.asset?.fields?.vehicle_class?.value != 'LCV'
        })

        const next_button_active = computed(() => {

            let active = true

            for (let f in state.vehicle_form.fields) {
                if (state.vehicle_form.fields[f].required && !state.vehicle_form.fields[f].value) active = false
            }

            return active
        })

        const user_is_auctioneer = computed(() => {
            const asset_auctioneer_id = VEHICLE?.vehicle?.asset?.auctioneer_id ? VEHICLE?.vehicle?.asset?.auctioneer_id
                : VEHICLE?.vehicle?.asset?.auctioneer?.id ? VEHICLE?.vehicle?.asset?.auctioneer?.id
                    : null

            return asset_auctioneer_id ? USER_STORE.userIsAuctioneerOfAsset({asset_auctioneer_id: asset_auctioneer_id})
                : false
        })

        const state = reactive({
            existing_vehicle_check_complete: vehicle_data?.value?.vrm ? true : false,
            existing_vehicle_modal_active: false,
            existing_vehicle_modal_heading: null,
            existing_vehicle_modal_body: null,
            existing_vehicle_action: null,
            find_vehicle_error: null,
            search_vrm: '',
            search_vrm_error: false,
            search_mileage: null,
            search_mileage_error: false,
            search_message: null,
            search_loading: false,
            existing_finance_error_show: false,
            existing_finance_error: {
                heading: null,
                body: null,
                data: null,
                disable_progress: false
            },
            vehicle_form: {
                show_fields: false,
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.vehicle_form.fields) {
                        if (state.vehicle_form.fields[f].value != state.vehicle_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    vrm: {
                        active: true,
                        label: 'VRM',
                        type: 'text',
                        value: vehicle_data?.value?.vrm || '',
                        stored_value: vehicle_data?.value?.vrm || '',
                        required: true,
                        pristine: vehicle_data?.value?.vrm ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    vin: {
                        active: true,
                        label: 'VIN',
                        type: 'text',
                        value: vehicle_data?.value?.vin || '',
                        stored_value: vehicle_data?.value?.vin || '',
                        required: true,
                        pristine: vehicle_data?.value?.vin ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    make: {
                        active: true,
                        label: 'Make',
                        type: 'text',
                        value: vehicle_data?.value?.make || '',
                        stored_value: vehicle_data?.value?.make || '',
                        required: true,
                        pristine: vehicle_data?.value?.make ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    model: {
                        active: true,
                        label: 'Model',
                        type: 'text',
                        value: vehicle_data?.value?.model || '',
                        stored_value: vehicle_data?.value?.model || '',
                        required: true,
                        pristine: vehicle_data?.value?.model ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    variant: {
                        active: true,
                        label: 'Variant',
                        type: 'text',
                        value: vehicle_data?.value?.variant || '',
                        stored_value: vehicle_data?.value?.variant || '',
                        required: false,
                        pristine: vehicle_data?.value?.variant ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    colour: {
                        active: true,
                        label: 'Colour',
                        type: 'text',
                        value: vehicle_data?.value?.colour || '',
                        stored_value: vehicle_data?.value?.colour || '',
                        required: true,
                        pristine: vehicle_data?.value?.colour ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    first_registered: {
                        active: true,
                        label: 'First registered',
                        type: 'datepicker',
                        min_date: null,
                        time_enabled: false,
                        date_format: 'dd-MM-yyyy',
                        date_clearable: true,
                        value: vehicle_data?.value?.first_registered || '',
                        stored_value: vehicle_data?.value?.first_registered || '',
                        required: true,
                        pristine: vehicle_data?.value?.first_registered ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    registration_country: {
                        active: true,
                        label: 'Registration country',
                        type: 'select',
                        value: vehicle_data?.value?.registration_country || 'GBR',
                        stored_value: vehicle_data?.value?.registration_country || '',
                        options: config.country_alpha_codes,
                        required: true,
                        pristine: vehicle_data?.value?.registration_country ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    manufacture_year: {
                        active: true,
                        label: 'Manufacture year',
                        type: 'number',
                        value: vehicle_data?.value?.manufacture_year || '',
                        stored_value: vehicle_data?.value?.manufacture_year || '',
                        required: true,
                        pristine: vehicle_data?.value?.manufacture_year ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    body: {
                        active: true,
                        label: 'Body type',
                        type: 'text',
                        value: vehicle_data?.value?.body || '',
                        stored_value: vehicle_data?.value?.body || '',
                        required: true,
                        pristine: vehicle_data?.value?.body ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    doors: {
                        active: true,
                        label: 'Doors',
                        type: 'number',
                        value: vehicle_data?.value?.doors || '',
                        stored_value: vehicle_data?.value?.doors || '',
                        required: false,
                        pristine: vehicle_data?.value?.doors ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    engine_size: {
                        active: true,
                        label: 'Engine size CC',
                        type: 'number',
                        value: vehicle_data?.value?.engine_size || '',
                        stored_value: vehicle_data?.value?.engine_size || '',
                        required: false,
                        pristine: vehicle_data?.value?.engine_size ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    engine_number: {
                        active: true,
                        label: 'Engine number',
                        type: 'text',
                        value: vehicle_data?.value?.engine_number || '',
                        stored_value: vehicle_data?.value?.engine_number || '',
                        required: false,
                        pristine: vehicle_data?.value?.engine_number ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    fuel_type: {
                        active: true,
                        label: 'Fuel type',
                        type: 'text',
                        value: vehicle_data?.value?.fuel_type || '',
                        stored_value: vehicle_data?.value?.fuel_type || '',
                        required: true,
                        pristine: vehicle_data?.value?.fuel_type ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    transmission: {
                        active: true,
                        label: 'Transmission',
                        type: 'text',
                        value: vehicle_data?.value?.transmission || '',
                        stored_value: vehicle_data?.value?.transmission || '',
                        required: true,
                        pristine: vehicle_data?.value?.transmission ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    vehicle_class: {
                        active: true,
                        label: 'Vehicle class',
                        type: 'select',
                        value: vehicle_data?.value?.vehicle_class || 'CAR',
                        stored_value: vehicle_data?.value?.vehicle_class || '',
                        options: [
                            {
                                value: 'CAR',
                                label: 'Passenger'
                            },
                            {
                                value: 'LCV',
                                label: 'Commercial'
                            }
                        ],
                        required: true,
                        pristine: vehicle_data?.value?.vehicle_class ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    mileage_unit: {
                        active: true,
                        label: 'Odometer unit',
                        type: 'select',
                        options: [
                            { value: 'miles', label: 'Miles' },
                            { value: 'kilometers', label: 'Kilometers' },
                        ],
                        value: vehicle_data?.value?.mileage_unit || 'miles',
                        stored_value: vehicle_data?.value?.mileage_unit || 'miles',
                        required: true,
                        pristine: vehicle_data?.value?.mileage_unit ? false : true,
                        valid: vehicle_data?.value?.milemileage_unitage ? true : false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    mileage: {
                        active: true,
                        label: 'Mileage',
                        type: 'number',
                        value: vehicle_data?.value?.mileage || '',
                        stored_value: vehicle_data?.value?.mileage || '',
                        required: true,
                        pristine: vehicle_data?.value?.mileage ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    mileage_status: {
                        active: true,
                        label: 'Odometer warranted?',
                        type: 'select',
                        options: [
                            { value: 'unwarranted', label: 'Unwarranted' },
                            { value: 'warranted', label: 'Warranted' }
                        ],
                        value: vehicle_data?.value?.mileage_status || 'warranted',
                        stored_value: vehicle_data?.value?.mileage_status || 'warranted',
                        required: false,
                        pristine: vehicle_data?.value?.mileage_status ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    drive: {
                        active: true,
                        label: 'Drive',
                        type: 'select',
                        options: [
                            { value: 'RHD', label: 'RHD' },
                            { value: 'LHD', label: 'LHD' },
                        ],
                        value: vehicle_data?.value?.drive || 'RHD',
                        stored_value: vehicle_data?.value?.drive || 'RHD',
                        required: true,
                        pristine: vehicle_data?.value?.drive ? false : true,
                        valid: vehicle_data?.value?.drive ? true : false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    cap_clean: {
                        active: true,
                        label: 'CAP clean',
                        type: 'hidden',
                        value: vehicle_data?.value?.cap_clean || '',
                        stored_value: vehicle_data?.value?.cap_clean || '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    cap_average: {
                        active: true,
                        label: 'CAP average',
                        type: 'hidden',
                        value: vehicle_data?.value?.cap_average || '',
                        stored_value: vehicle_data?.value?.cap_average || '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    cap_poor: {
                        active: true,
                        label: 'CAP poor',
                        type: 'hidden',
                        value: vehicle_data?.value?.cap_poor || '',
                        stored_value: vehicle_data?.value?.cap_poor || '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    /*
                    mot_expiry_external: {
                        active: true,
                        label: 'MOT Expiry',
                        type: 'hidden',
                        value: vehicle_data?.value?.mot_expiry_external || '',
                        stored_value: vehicle_data?.value?.mot_expiry_external || '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    */
                    mot_status: {
                        active: true,
                        label: 'MOT Status',
                        type: 'select',
                        options: [
                            { value: 'required_and_current', label: 'Requires an MOT - has a current MOT' },
                            { value: 'required_and_expired', label: 'Requires an MOT - does not have a current MOT' },
                            { value: 'required_and_unknown', label: 'Requires an MOT - TBC' },
                            { value: 'not_required', label: 'Does not require an MOT' }
                        ],
                        value: vehicle_data?.value?.mot_status || 'required_and_current',
                        stored_value: vehicle_data?.value?.mot_status || 'required_and_current',
                        required: true,
                        pristine: vehicle_data?.value?.mot_status ? false : true,
                        valid: vehicle_data?.value?.mot_status ? true : false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: false
                    },
                    mot_expiry_external: {
                        active: vehicle_data?.value?.mot_status !== 'not_required',
                        label: 'DVSA MOT expiry date',
                        type: 'datepicker',
                        min_date: null,
                        time_enabled: false,
                        date_format: 'dd-MM-yyyy',
                        date_clearable: true,
                        value: vehicle_data?.value?.mot_expiry_external || '',
                        stored_value: vehicle_data?.value?.mot_expiry_external || '',
                        required: false,
                        pristine: vehicle_data?.value?.mot_expiry_external ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: user_is_auctioneer.value,
                        disabled: true
                    },
                    mot_expiry_manual: {
                        active: vehicle_data?.value?.mot_status !== 'not_required',
                        label: 'Updated MOT expiry date (if different to DVSA)',
                        type: 'datepicker',
                        min_date: null,
                        time_enabled: false,
                        date_format: 'dd-MM-yyyy',
                        date_clearable: true,
                        value: vehicle_data?.value?.mot_expiry_manual || '',
                        stored_value: vehicle_data?.value?.mot_expiry_manual || '',
                        required: false,
                        pristine: vehicle_data?.value?.mot_expiry_manual ? false : true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                }
            },
        })

        const clearVehicleDataError = () => {
            state.existing_finance_error = {
                heading: null,
                body: null,
                data: null,
                disable_progress: false
            }
            state.existing_finance_error_show = false
        }

        const setVehicleDataError = (heading = null, body = null, data = null, disable_progress = false) => {
            state.existing_finance_error = {
                heading: heading,
                body: body,
                data: data,
                disable_progress: disable_progress
            }
            state.existing_finance_error_show = true
        }

        const setVehicleData = vehicle => {
            if (!vehicle) return

            for (let field in state.vehicle_form.fields) {

                if (field == 'mileage') {
                    state.vehicle_form.fields[field].value = state.search_mileage
                } else if (vehicle[field]) {
                    state.vehicle_form.fields[field].value = vehicle[field]

                    state.vehicle_form.fields[field].disabled = user_is_auctioneer.value
                        ? false
                        : !state.vehicle_form.fields[field].editable
                }
                state.vehicle_form.fields[field].pristine = false
            }

            if (state.vehicle_form.fields.vin.value) {
                isValid(state.vehicle_form.fields)
                state.vehicle_form.show_fields = true
            }
        }

        const hideFindVehicleError = () => {
            state.find_vehicle_error = null
        }

        const showFindVehicleError = message => {
            state.find_vehicle_error = message
        }

        const checkExistingVehicles = async current_action => {

            state.search_vrm_error = false
            state.search_mileage_error = false

            let search_error = ''

            if (!state.search_vrm) {
                state.search_vrm_error = true
                search_error = 'Vehicle number plate is a required field'
            }

            if (!state.search_mileage) {
                state.search_mileage_error = true
                search_error = search_error.length > 0 ? 'Vehicle number plate and mileage are required fields' : 'Mileage is a required field'
            }

            if (search_error.length > 0) return SYSTEM_STORE.addError(search_error)
            // assign teh target action
            state.existing_vehicle_action = current_action
            // ignore this if the check has already been done
            if (state.existing_vehicle_check_complete) return existingVehicleAction()
            // proceed with check
            const vrm_check = await VehicleService.vrmCheck(state.search_vrm || state.vehicle_form.fields.vrm.value, AUTH_STORE.user?.id)
            // flag as checked
            state.existing_vehicle_check_complete = true
            // respond to check
            if (Array.isArray(vrm_check) && vrm_check.length > 0) {
                state.existing_vehicle_modal_heading = `Existing Vehicle`
                state.existing_vehicle_modal_body = `You already have a ${vrm_check[0].vehicle_make} ${vrm_check[0].vehicle_model} with the vrm \'${vrm_check[0].vehicle_vrm}\'. Are you sure you want to add it again?`
                state.existing_vehicle_modal_active = true
            } else {
                existingVehicleAction()
            }
        }

        const existingVehicleAction = () => {
            switch(state.existing_vehicle_action){
                case 'search':
                    findVehicle()
                    break
                case 'submit':
                    submitStep()
                    break
            }
        }

        const cancelSearch = () => {
            state.existing_vehicle_check_complete = false
            state.existing_vehicle_modal_active = false
            state.existing_vehicle_modal_heading = null
            state.existing_vehicle_modal_body = null
            state.existing_vehicle_action = null
            state.search_vrm = null
        }

        const findVehicle = async () => {

            if (state.search_loading) return

            let search_error = ''

            if (!state.search_vrm) {
                search_error = 'Vehicle number plate is a required field'
            }

            if (!state.search_mileage) {
                search_error = search_error.length > 0 ? 'Vehicle number plate and mileage are required fields' : 'Mileage is a required field'
            }

            if (search_error.length > 0) return SYSTEM_STORE.addError(search_error)

            hideFindVehicleError()

            state.search_loading = true
            state.existing_vehicle_modal_active = false
            state.existing_vehicle_modal_heading = null
            state.existing_vehicle_modal_body = null
            state.existing_vehicle_action = null

            clearVehicleDataError()

            try {

                state.search_message = 'Please wait a moment whilst we lookup this vehicle\'s information.'
                
                const vehicle_data = await VehicleService.valuation(state.search_vrm, state.search_mileage, 1)
                const mot_expiry_date = vehicle_data.make ? await VehicleService.getMotExpiryForVrm(state.search_vrm, vehicle_data.make)
                    : null

                if (mot_expiry_date) state.vehicle_form.fields.mot_expiry_external.value = mot_expiry_date
                
                if (!vehicle_data?.vin) {
                    showFindVehicleError(`Vehicle not found. Please try another VRM or enter manually`)
                } else {
                    setCapValues(vehicle_data)
                    setVehicleData(vehicle_data)
                }

            } catch (error) {

                console.error(error)

                SYSTEM_STORE.createInteraction('ERROR', ROUTE.fullPath, 'CAP HPI vehicle lookup error', { error: error || 'no error object'})
                
                switch(error?.status) {
                    case 422:
                        setVehicleDataError('Existing Finance Alert', 'This vehicle has existing finance so it does not qualify for a new loan agreement', error?.data || null, true)
                        break;
                    default:
                        showFindVehicleError(`There was an error finding this vehicle. Please try another VRM or enter manually`)
                }

            } finally {
                state.search_loading = false
                state.search_message = null
            }
        }

        const setCapValues = vehicle_data => {

            const valuations = vehicle_data?.raw?.RequestResults?.Asset?.SupplementaryAssetData?.CAPLiveValuation?.MileageAndConditionBasedValuation?.ConditionBasedValuations?.ConditionBasedValuation || []

            if (!Array.isArray(valuations) || valuations.length == 0) return

            const cap_clean = valuations.find(v => v.Description == 'Clean')
            const cap_average = valuations.find(v => v.Description == 'Average')
            const cap_poor = valuations.find(v => v.Description == 'Below Average')

            state.vehicle_form.fields.cap_clean.value = !isNaN(cap_clean?.TradeValue) ? cap_clean?.TradeValue * 100 : null
            state.vehicle_form.fields.cap_average.value = !isNaN(cap_average?.TradeValue) ? cap_average?.TradeValue * 100 : null
            state.vehicle_form.fields.cap_poor.value = !isNaN(cap_poor?.TradeValue) ? cap_poor?.TradeValue * 100 : null
        }

        const submitStep = async () => {
            // fields will automatically create their own validation states
            if (state.vehicle_form.loading || !isValid(state.vehicle_form.fields)) return

            // if the value is present and unchaned, just skip to the next step
            if (!state.vehicle_form.has_changes) {
                VEHICLE.activateNextStep()
                return
            }

            state.vehicle_form.loading = true

            try {

                const asset_payload = getPayload(state.vehicle_form.fields)
                const vehicle_update = await VEHICLE.update(VEHICLE.vehicle.id, asset_payload)
                VEHICLE.activateNextStep()
            } catch (error) {
                console.error('submitStep', error)
                // TODO handle error
            }

            state.vehicle_form.loading = false
        }

        const setShowFields = () => {
            // make sure we have an item
            if (!vehicle_data) return
            // if we have a VIN, show the fields
            if (vehicle_data?.vin) state.vehicle_form.show_fields = true
        }

        const runVehicleChecks = async () => {
            try {
                const checks = await VEHICLE.runChecks(VEHICLE.vehicle.id)
            } catch (error) {
                console.error('VEHICLE.runChecks error', error)
            }
        }

        watch(
            () => vehicle_data.value,
            (new_value, old_value) => {
                setVehicleData(vehicle_data.value)
            }
        )

        watch(
            () => state.vehicle_form.fields.vrm.value,
            (new_value, old_value) => {
                state.search_vrm = state.vehicle_form.fields.vrm.value
            }
        )

        watch(
            () => state.vehicle_form.fields.mileage.value,
            (new_value, old_value) => {
                state.search_mileage = state.vehicle_form.fields.mileage.value
            }
        )

        watch(
            () => state.search_vrm,
            (new_value, old_value) => {
                if (new_value.length > 0) state.search_vrm_error = false
            }
        )

        watch(
            () => state.search_mileage,
            (new_value, old_value) => {
                if (new_value) state.search_mileage_error = false
            }
        )

        watch (
            () => state.vehicle_form.fields.mot_status.value,
            (new_value) => {
                state.vehicle_form.fields.mot_expiry_external.active = new_value !== 'not_required'
                state.vehicle_form.fields.mot_expiry_manual.active = new_value !== 'not_required'
            }
        )

        onMounted(() => {
            setShowFields()
            isValid(state.vehicle_form.fields)
            if (state.vehicle_form.fields.vin.value) state.vehicle_form.show_fields = true
        })

        return {
            VEHICLE,
            state,
            findVehicle,
            submitStep,
            clearVehicleDataError,
            checkExistingVehicles,
            cancelSearch,
            existingVehicleAction,
            next_button_active,
            user_is_auctioneer
        }
    }
}

// VN21ENE

// ben.leah+acme-g-owner@hobbsparker.co.uk
// ben.leah+hp-g-owner@hobbsparker.co.uk
</script>

